/* eslint-disable react/display-name */
import * as React from "react";
import PropTypes from "prop-types";
import cs from "classnames";
import { LinkProvider } from "@lululemon/ecom-pattern-library-link-provider";
import { LinkGenerator } from "@lululemon/ecom-pattern-library-link-generator";
import { Button } from "@lululemon/ecom-pattern-library-button";

import BraQuizDataContext from "./../utils/quizDataContext";
import styles from "./quiz-button.module.scss";
import { trackBqPDPInteraction } from "../utils/index.js";

const QuizButton = React.forwardRef(
  (
    {
      isBackButton,
      isStickyButton,
      isTileButton,
      isVisible,
      onClick,
      text,
      ...rest
    },
    ref
  ) => {
    if (!text) {
      return null;
    }

    if (isBackButton) {
      return (
        <LinkProvider component="button">
          <LinkGenerator
            onClick={onClick}
            className={cs(
              "lll-link-primary",
              "lll-text-body-2",
              styles.quizButtonBack,
              {
                [styles.quizButtonBackOffscreen]: !isVisible,
              }
            )}
            text={text}
            ref={ref}
            aria-hidden={!isVisible}
            {...rest}
          >
            {text}
          </LinkGenerator>
        </LinkProvider>
      );
    }

    const nextStepButton = (
      <Button
        className={cs({
          [styles.quizButtonSticky]: isStickyButton,
          [styles.quizButtonNext]: !isStickyButton,
          [styles.quizButtonNextForTile]: isTileButton && !isStickyButton,
        })}
        disabled={!isVisible}
        kind="secondary"
        onClick={onClick}
        block={isStickyButton}
        ref={ref}
        {...rest}
      >
        {text}
      </Button>
    );

    return isStickyButton ? (
      <div
        data-testid="sticky-button-box"
        className={styles.quizButtonStickyBox}
      >
        {nextStepButton}
      </div>
    ) : (
      nextStepButton
    );
  }
);

export const QuizDrawerButtons = ({
  isBackButtonVisible,
  isLastQuestion,
  isNextButtonActive,
  onNextButtonClick,
  onBackButtonClick,
  activeQuestionIndex,
  ...rest
}) => {
  const {
    embeddedCopies: { pdpSeeResults, pdpNextStepText, pdpPreviousStepText },
  } = React.useContext(BraQuizDataContext);
  const nextButtonText = isLastQuestion ? pdpSeeResults : pdpNextStepText;

  const nextClickHandler = () => {
    onNextButtonClick();

    const nextDetails = [
      "lets-start-with-your-size-1",
      "lets-talk-cups-2",
      "next-up-band-fit-3",
      "one-final-question-4",
    ];

    trackBqPDPInteraction({
      id: "quiz:bra-size",
      type: "Button",
      text: isLastQuestion ? "see-results" : "next",
      attributes: {
        details: nextDetails[activeQuestionIndex],
      },
    });
  };

  return (
    <div
      data-testid="quiz-embedded-buttons"
      className={styles.quizEmbeddedButtons}
    >
      <div className={styles.quizEmbeddedButtonsBox}>
        <QuizButton
          block={!isBackButtonVisible}
          isVisible={isNextButtonActive}
          text={nextButtonText}
          onClick={nextClickHandler}
          tabIndex={isNextButtonActive ? "0" : "-1"}
          kind="primary"
          {...rest}
        />
        {isBackButtonVisible && (
          <QuizButton
            isVisible
            kind="tertiary"
            onClick={onBackButtonClick}
            text={pdpPreviousStepText}
            {...rest}
          />
        )}
      </div>
    </div>
  );
};

QuizButton.defaultProps = {
  isBackButton: false,
  isStickyButton: false,
  isTileButton: false,
  isVisible: false,
  onClick: () => {},
};

QuizButton.propTypes = {
  isBackButton: PropTypes.bool,
  isStickyButton: PropTypes.bool,
  isTileButton: PropTypes.bool,
  isVisible: PropTypes.bool,
  onClick: PropTypes.func,
  text: PropTypes.string.isRequired,
};

QuizDrawerButtons.defaultProps = {
  isBackButtonVisible: false,
  isLastQuestion: false,
  isNextButtonActive: false,
  onNextButtonClick: () => {},
  onBackButtonClick: () => {},
  activeQuestionIndex: 0,
};

QuizDrawerButtons.propTypes = {
  isBackButtonVisible: PropTypes.bool,
  isLastQuestion: PropTypes.bool,
  isNextButtonActive: PropTypes.bool,
  onNextButtonClick: PropTypes.func,
  onBackButtonClick: PropTypes.func,
  activeQuestionIndex: PropTypes.number,
};

export default React.memo(QuizButton);
